import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Button, Stack, SxProps } from '@mui/material';
import { useEffect, useMemo, useState, Suspense } from 'react';
import { PoiSuspenseFallback } from './PoiSuspenseFallback';
import { PoiBackButton } from './PoiBackButton';
import { PoiHeader } from './PoiHeader';
import { PoiReviewControls } from './PoiReviewControls';
import {
  PointOfInterest,
  useSelectedPoiUpdatedSubscription,
  useSelectedPointOfInterestQuery,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { DetailsTab, GeneralTab } from './Tabs';
import { DynamicPoiPanel } from './DynamicPoiPanel/DynamicPoiPanel';
import { Comments } from '@/components/shared/Comments';
import { Logs } from './Logs/Logs';
import { useAtomValue, useSetAtom } from 'jotai';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { useIsolateCustomBlock } from '@/components/Analysis/modules/CustomBlocks/hooks';
import { useFeatureFlag, useIsAbyssUser } from '@/hooks';
import * as state from '@/components/Analysis/state';

interface TabPanelProps {
  children?: React.ReactNode;
  id: number;
  selectedTabId: number;
}

function PoiCommentsAndLogsButtons({
  selectedTab,
  setSelectedTab,
  showLogs = true,
}: {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  showLogs?: boolean;
}) {
  return (
    <Stack direction="row" spacing={2} sx={{ background: abyssColors.primary[100], p: 1 }}>
      <Button
        variant="contained"
        sx={{
          color: selectedTab === 'comments' ? 'white' : 'inherit',
          backgroundColor: selectedTab === 'comments' ? abyssColors.primary[500] : 'inherit',
        }}
        onClick={() => setSelectedTab('comments')}
      >
        Comments
      </Button>
      {showLogs && (
        <Button
          variant="contained"
          sx={{
            color: selectedTab === 'logs' ? 'white' : 'inherit',
            backgroundColor: selectedTab === 'logs' ? abyssColors.primary[500] : 'inherit',
          }}
          onClick={() => setSelectedTab('logs')}
        >
          Logs
        </Button>
      )}
    </Stack>
  );
}

export function TabContent(props: TabPanelProps) {
  const { children, selectedTabId, id, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={selectedTabId !== id}
      id={`simple-tabpanel-${id}`}
      style={{ flex: '1 1 auto' }}
      aria-labelledby={`simple-tab-${id}`}
      {...other}
    >
      {children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

type Props = {
  selectedPoiId: string;
  closeHandler: () => void;
  onDeleteHandler?: () => void;
  disableEdit?: boolean;
  pointOfInterestDetails?: PointOfInterest;
  resetFocus?: boolean;
  enableNavigation?: boolean;
  sx?: SxProps;
  showBackButton?: boolean;
  showHeader?: boolean;
  showReviewControls?: boolean;
};

export const SelectedPoiContent = ({
  selectedPoiId,
  closeHandler,
  onDeleteHandler,
  disableEdit,
  resetFocus,
  enableNavigation,
  sx,
  showBackButton = true,
  showHeader = true,
  showReviewControls = true,
}: Props) => {
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const [selectedPoiInfo, setSelectedPoiInfo] = useState<PointOfInterest | undefined>(undefined);
  const [selectedTab, setSelectedTab] = useState<string>('comments');
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  // Reset selectedPoiInfo when selectedPoiId changes to prevent stale data display
  useEffect(() => {
    setSelectedPoiInfo(undefined);
  }, [selectedPoiId]);
  const isAbyssUser = useIsAbyssUser();
  const isWorkInProgress = useFeatureFlag('work-in-progress');
  const { loading: selectedPoiInfoLoading, refetch } = useSelectedPointOfInterestQuery({
    variables: {
      pointOfInterestId: selectedPoiId,
    },

    skip: !selectedPoiId,
    notifyOnNetworkStatusChange: true, // onCompleted is not triggered after refetch if this is false
    onCompleted: (response) => {
      const data = response?.pointOfInterest;
      if (data) {
        if (data?.files && data.files.length > 0) {
          setSelectedPoiInfo({ ...data, files: data.files } as PointOfInterest);
          isolateWorkScope({ ...data, files: data.files } as PointOfInterest);
        } else {
          setSelectedPoiInfo(data as PointOfInterest);
          isolateWorkScope(data as PointOfInterest);
        }
      } else {
        setSelectedPoiInfo(undefined);
        // reset selectedPoiId when deleted / not found
        setSelectedPointOfInterestId(undefined);
      }
    },
  });

  useSelectedPoiUpdatedSubscription({
    variables: {
      poiID: selectedPoiId,
    },
    onData: () => {
      refetch();
    },
  });

  const { isolateWorkScope } = useIsolateCustomBlock({ resetFocus });

  const tabsData = useMemo(
    () => [
      {
        id: 0,
        label: 'Overview',
        component: selectedPoiInfo && (
          <Suspense fallback={<PoiSuspenseFallback />}>
            <GeneralTab
              sx={sx}
              selectedPoiInfo={selectedPoiInfo}
              selectedPoiInfoLoading={selectedPoiInfoLoading}
            />
          </Suspense>
        ),
      },
      {
        id: 1,
        label: 'Details',
        component: selectedPoiInfo && (
          <Suspense fallback={<PoiSuspenseFallback />}>
            <DetailsTab disableEdit={disableEdit} selectedPoiInfo={selectedPoiInfo} sx={sx} />
          </Suspense>
        ),
      },
      {
        id: 2,
        label: isAbyssUser ? 'Comments/logs' : 'Comments',
        component: (
          <Suspense fallback={<PoiSuspenseFallback />}>
            {selectedTab === 'comments' && (
              <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <PoiCommentsAndLogsButtons
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    showLogs={isAbyssUser}
                  />
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Comments referenceId={selectedPoiId} collectionName="pointOfInterests" />
                </Box>
              </Box>
            )}
            {isAbyssUser && selectedTab === 'logs' && (
              <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <PoiCommentsAndLogsButtons
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    showLogs={isAbyssUser}
                  />
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Logs
                    referenceId={selectedPoiId}
                    collectionName="PointOfInterest"
                    poiType={selectedPoiInfo?.type}
                    isLogsTab={selectedTab === 'logs'}
                  />
                </Box>
              </Box>
            )}
          </Suspense>
        ),
      },
    ],
    [selectedPoiInfo, selectedPoiInfoLoading, disableEdit, sx, isAbyssUser, selectedTab, selectedPoiId]
  );

  const [selectedTabId, setSelectedTabId] = useState(tabsData[0].id);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabId(newValue);
  };

  const templateOfSelectedPoi = useMemo(
    () => allPointOfInterestTemplates.find(({ id }) => id === selectedPoiInfo?.template?.id),
    [allPointOfInterestTemplates, selectedPoiInfo?.template?.id]
  );

  if (!selectedPoiInfo) {
    return <PoiSuspenseFallback />;
  }

  return (
    <Stack
      direction="column"
      sx={{ width: '100%', height: '100%', backgroundColor: abyssColors.primary[50] }}
    >
      {showBackButton && <PoiBackButton onClick={closeHandler} />}
      {showHeader && (
        <PoiHeader
          selectedPoiInfo={selectedPoiInfo}
          enableNavigation={enableNavigation}
          styleSx={sx}
          onDelete={onDeleteHandler ?? closeHandler}
        />
      )}
      {showReviewControls && <PoiReviewControls selectedPoiInfo={selectedPoiInfo} />}

      {isWorkInProgress && templateOfSelectedPoi?.panelConfig ? (
        <DynamicPoiPanel
          config={templateOfSelectedPoi.panelConfig}
          poi={selectedPoiInfo}
          template={templateOfSelectedPoi}
        />
      ) : (
        <>
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
              '& button': { textTransform: 'none', fontWeight: 400 },
              '& .Mui-selected': { fontWeight: 500 },
            }}
          >
            <Tabs
              value={selectedTabId}
              onChange={handleChange}
              variant="fullWidth"
              sx={{
                '@media (max-width: 1728px)': {
                  minHeight: '36px',
                  '& button': {
                    padding: 0,
                    minHeight: '36px',
                    minWidth: 0,
                  },
                },
              }}
            >
              {tabsData.map((tab) => (
                <Tab label={tab.label} {...a11yProps(tab.id)} key={tab.id} />
              ))}
            </Tabs>
          </Box>
          {tabsData.map((tab) => (
            <TabContent key={tab.id} selectedTabId={selectedTabId} id={tab.id}>
              {tab.component}
            </TabContent>
          ))}
        </>
      )}
    </Stack>
  );
};
